import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import Logo from "./Logo";

const LINKS = [
  { label: "Services", href: "#services", testid: "nav-services-link" },
  { label: "Manifesto", href: "#manifesto", testid: "nav-manifesto-link" },
  { label: "Founder", href: "#founder", testid: "nav-founder-link" },
  { label: "See It in Action", href: "#casestudy", testid: "nav-casestudy-link" },
];

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) window.__lenis ? window.__lenis.scrollTo(el, { offset: -80 }) : el.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    setTimeout(() => scrollTo(href), 10);
  };

  return (
    <motion.header
      data-testid="nav-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 inset-x-0 z-50"
    >
      <div
        className={`mx-auto max-w-7xl px-4 sm:px-6 transition-all duration-500 ${
          scrolled ? "mt-2 sm:mt-3" : "mt-0"
        }`}
      >
        <div
          className={`flex items-center justify-between rounded-2xl px-4 sm:px-5 py-3 transition-all duration-500 ${
            scrolled
              ? "glass-light border border-fuchsia-500/10"
              : "bg-transparent border border-transparent"
          }`}
        >
          <button onClick={() => go("#top")} className="cursor-pointer" data-testid="nav-logo-button">
            <Logo variant={scrolled ? "light" : "dark"} />
          </button>

          <nav className="hidden md:flex items-center gap-1">
            {LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={l.testid}
                onClick={() => go(l.href)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  scrolled
                    ? "text-slate-600 hover:text-fuchsia-600"
                    : "text-slate-200 hover:text-white"
                }`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              data-testid="nav-book-button"
              onClick={() => go("#contact")}
              className="btn-glow hidden sm:inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white"
              style={{ background: "linear-gradient(100deg,#FF4FD8,#E933C6 55%,#8B5CF6)" }}
            >
              Book Consultation
            </button>
            <button
              onClick={() => setOpen((v) => !v)}
              className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-xl glass-light text-slate-800"
              data-testid="nav-mobile-toggle"
              aria-label="Menu"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="md:hidden mt-2 glass-light rounded-2xl p-3"
              data-testid="nav-mobile-menu"
            >
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className="block w-full text-left px-4 py-3 text-slate-700 font-medium hover:bg-fuchsia-50 rounded-xl"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => go("#contact")}
                className="mt-2 w-full rounded-xl px-4 py-3 text-white font-semibold flex items-center justify-center gap-2"
                style={{ background: "linear-gradient(100deg,#FF4FD8,#E933C6 55%,#8B5CF6)" }}
              >
                <Phone size={16} /> Book Consultation
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Navbar;
